<div class="page-container" *ngIf="!loading && project">
  <!-- Header -->
  <div class="wizard-header">
    <button mat-icon-button (click)="goBack()" matTooltip="Zurück zum Projekt">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div class="header-info">
      <h1>Sicherheitsbewertung</h1>
      <span class="project-label">{{project.name}}</span>
    </div>
  </div>

  <!-- Progress Bar -->
  <div class="progress-bar">
    <div class="progress-steps">
      <div class="progress-step" *ngFor="let p of phases; let i = index"
           [class.active]="i === phase"
           [class.completed]="i < phase"
           (click)="i < phase ? phase = i : null">
        <div class="step-number">
          <mat-icon *ngIf="i < phase">check</mat-icon>
          <span *ngIf="i >= phase">{{i + 1}}</span>
        </div>
        <span class="step-label">{{p.label}}</span>
      </div>
    </div>
    <div class="progress-track">
      <div class="progress-fill" [style.width.%]="(phase / (phases.length - 1)) * 100"></div>
    </div>
  </div>

  <!-- ═══════ Phase 0: Welcome ═══════ -->
  <div *ngIf="phase === 0" class="phase-content">
    <div class="welcome-card">
      <div class="welcome-icon">
        <mat-icon>security</mat-icon>
      </div>
      <h2>CRA / IEC 62443 – Lieferanten-Checkliste</h2>
      <p>Dieses Tool führt Sie als <strong>Lieferant / Dienstleister</strong> durch eine strukturierte Prüfung,
         ob alle Cybersicherheitsanforderungen für die Kundenübergabe erfüllt sind – basierend auf dem
         <strong>Cyber Resilience Act (CRA)</strong> und der <strong>IEC 62443</strong>.</p>
      <div class="info-box blue">
        <mat-icon>info</mat-icon>
        <div>
          <strong>Was wird geprüft?</strong>
          <ul>
            <li>CRA-Pflichten als Lieferant (Anhang I – Produktanforderungen &amp; Schwachstellenbehandlung)</li>
            <li>IEC 62443-2-4 Dienstleister-Anforderungen (SP.01–SP.07)</li>
            <li>Netzwerk-Segmentierung (Zonen &amp; Conduits nach IEC 62443-3-2)</li>
            <li>Security Level &amp; 7 Foundational Requirements (IEC 62443-3-3)</li>
            <li>Vollständigkeit der Übergabe-Dokumentation</li>
          </ul>
        </div>
      </div>
      <div class="info-box amber">
        <mat-icon>warning</mat-icon>
        <div>
          <strong>Hinweis:</strong> Dieses Tool ersetzt nicht die Konsultation der kostenpflichtigen IEC 62443-Norm.
          Die konkreten System Requirements (SRs) und Requirement Enhancements (REs) müssen aus der Originalnorm entnommen werden.
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Phase 1: Projektkontext ═══════ -->
  <div *ngIf="phase === 1" class="phase-content">
    <h2>Projektkontext</h2>

    <div class="form-section">
      <label class="form-label">Was liefern wir dem Kunden? *</label>
      <div class="radio-group">
        <div class="radio-option" *ngFor="let opt of [
          {value: 'sps_program', label: 'SPS-Programm (z.B. TIA Portal, CODESYS)'},
          {value: 'sps_and_hardware', label: 'SPS-Programm + Hardware (z.B. Siemens, Beckhoff)'},
          {value: 'complete_system', label: 'Komplettanlage / Schaltschrank + Programmierung'},
          {value: 'hmi_scada', label: 'HMI / SCADA-Lösung'},
          {value: 'retrofit', label: 'Retrofit / Umbau bestehender Anlage'}
        ]" [class.selected]="answers['company_type'] === opt.value"
           (click)="setAnswer('company_type', opt.value)">
          <div class="radio-dot" [class.active]="answers['company_type'] === opt.value"></div>
          <span>{{opt.label}}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <label class="form-label">Branche des Kunden</label>
      <div class="radio-group">
        <div class="radio-option" *ngFor="let opt of [
          {value: 'energy', label: 'Energie'},
          {value: 'water', label: 'Wasser / Abwasser'},
          {value: 'production', label: 'Produktion / Fertigung'},
          {value: 'food', label: 'Lebensmittel / Pharma'},
          {value: 'transport', label: 'Transport / Logistik'},
          {value: 'building', label: 'Gebäudeautomation'},
          {value: 'other', label: 'Sonstige'}
        ]" [class.selected]="answers['sector'] === opt.value"
           (click)="setAnswer('sector', opt.value)">
          <div class="radio-dot" [class.active]="answers['sector'] === opt.value"></div>
          <span>{{opt.label}}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <label class="form-label">Hat der Kunde Security-Anforderungen gestellt?</label>
      <div class="radio-group horizontal">
        <div class="radio-option" *ngFor="let opt of [
          {value: 'yes_62443', label: 'Ja, IEC 62443 / CRA'},
          {value: 'yes_generic', label: 'Ja, allgemeine Anforderungen'},
          {value: 'no', label: 'Nein / nicht explizit'},
          {value: 'unclear', label: 'Unklar'}
        ]" [class.selected]="answers['customer_security_req'] === opt.value"
           (click)="setAnswer('customer_security_req', opt.value)">
          <div class="radio-dot" [class.active]="answers['customer_security_req'] === opt.value"></div>
          <span>{{opt.label}}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <label class="form-label">Enthält die Lieferung netzwerkfähige Komponenten?</label>
      <div class="radio-group horizontal">
        <div class="radio-option" *ngFor="let opt of [
          {value: 'yes', label: 'Ja'},
          {value: 'no', label: 'Nein'},
          {value: 'partial', label: 'Teilweise'}
        ]" [class.selected]="answers['has_network_components'] === opt.value"
           (click)="setAnswer('has_network_components', opt.value)">
          <div class="radio-dot" [class.active]="answers['has_network_components'] === opt.value"></div>
          <span>{{opt.label}}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Phase 2: CRA + IEC 62443-2-4 ═══════ -->
  <div *ngIf="phase === 2" class="phase-content">
    <h2>CRA-Pflichten &amp; IEC 62443-2-4 Dienstleister-Anforderungen</h2>
    <p class="phase-desc">Welche der folgenden Anforderungen habt ihr für dieses Kundenprojekt bereits erfüllt?</p>

    <div class="info-box blue">
      <mat-icon>info</mat-icon>
      <div>
        <strong>Cyber Resilience Act (EU) 2024/2847:</strong> Als Lieferant von Produkten mit digitalen Elementen
        (SPS, HMI, Netzwerkkomponenten) seid ihr verpflichtet, die Anforderungen aus Anhang I zu erfüllen.
        Ergänzend definiert die <strong>IEC 62443-2-4</strong> konkrete Anforderungen an Dienstleister
        (Integratoren, SPS-Programmierer, Inbetriebnehmer).
      </div>
    </div>

    <div class="nis2-list">
      <label class="nis2-item" *ngFor="let m of nis2Measures"
             [class.checked]="answers[m.id]">
        <mat-checkbox [checked]="!!answers[m.id]" (change)="setAnswer(m.id, !answers[m.id])">
          <div class="nis2-text">
            <span class="nis2-title">{{m.text}}</span>
            <span class="nis2-ref">{{m.article}}</span>
          </div>
        </mat-checkbox>
      </label>
    </div>
  </div>

  <!-- ═══════ Phase 3: Zones & Conduits ═══════ -->
  <div *ngIf="phase === 3" class="phase-content">
    <h2>Zonen & Conduits</h2>
    <p class="phase-desc">Falls ihr Netzwerkkomponenten liefert oder die Anlage in ein Netzwerk integriert: Beantwortet die folgenden Fragen zur Netzwerkarchitektur beim Kunden.</p>

    <div class="info-box blue">
      <mat-icon>info</mat-icon>
      <div>
        <strong>IEC 62443-3-2:</strong> Als Lieferant/Integrator solltet ihr die Netzwerksegmentierung
        beim Kunden kennen und dokumentieren, in welche Zone eure Komponenten eingebunden werden.
        Dies ist Grundlage für die Security-Level-Zuordnung und die Härtungsempfehlungen.
      </div>
    </div>

    <div class="zone-questions">
      <div class="zone-question" *ngFor="let q of [
        {id: 'has_network_segmentation', text: 'Ist bekannt, ob das OT-Netzwerk des Kunden vom IT-Netzwerk getrennt ist?'},
        {id: 'has_dmz', text: 'Gibt es eine DMZ (Demilitarisierte Zone) zwischen IT und OT beim Kunden?'},
        {id: 'has_firewall_rules', text: 'Sind die benötigten Kommunikationswege unserer Komponenten dokumentiert (Ports, Protokolle)?'},
        {id: 'has_zone_diagram', text: 'Haben wir ein Zonen-/Netzwerkdiagramm für die Integration unserer Lieferung erstellt?'}
      ]">
        <span class="zone-text">{{q.text}}</span>
        <div class="zone-options">
          <button [class.active]="answers[q.id] === 'yes'" (click)="setAnswer(q.id, 'yes')">Ja</button>
          <button [class.active]="answers[q.id] === 'partial'" (click)="setAnswer(q.id, 'partial')">Teilw.</button>
          <button [class.active]="answers[q.id] === 'no'" (click)="setAnswer(q.id, 'no')">Nein</button>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Phase 4: Security Level ═══════ -->
  <div *ngIf="phase === 4" class="phase-content">
    <h2>Security Level Bestimmung</h2>

    <div class="info-box blue">
      <mat-icon>info</mat-icon>
      <div>
        <strong>IEC 62443-3-3 / IEC 62443-4-2:</strong> Security Levels beschreiben die Widerstandsfähigkeit
        gegen verschiedene Angreiferklassen. Die Zuordnung erfolgt pro Zone basierend auf einer
        Risikoanalyse (IEC 62443-3-2). Das Ziel-SL (SL-T) bestimmt, welche konkreten Anforderungen
        erfüllt werden müssen.
      </div>
    </div>

    <div class="sl-cards">
      <div class="sl-card" *ngFor="let sl of slKeys"
           [class.selected]="answers['target_sl'] === '' + sl"
           [style.border-color]="answers['target_sl'] === '' + sl ? slDescriptions[sl].color : ''"
           (click)="setAnswer('target_sl', '' + sl)">
        <div class="sl-badge" [style.background]="slDescriptions[sl].color">{{sl}}</div>
        <div class="sl-info">
          <strong>{{slDescriptions[sl].title}}</strong>
          <span class="sl-desc">{{slDescriptions[sl].description}}</span>
          <span class="sl-attacker">Angreiferprofil: {{slDescriptions[sl].attacker}}</span>
        </div>
      </div>
    </div>

    <div class="info-box amber">
      <mat-icon>lightbulb</mat-icon>
      <div>
        <strong>Empfehlung für Lieferanten:</strong> Für typische Industrieanlagen im DACH-Raum ist
        SL 2 ein praxistaugliches Ziel – auch wenn der Kunde kein konkretes SL vorgibt. Kunden in
        kritischer Infrastruktur (Energie, Wasser) fordern meist mindestens SL 2, idealerweise SL 3.
        Das gewählte SL bestimmt, welche Härtungsmaßnahmen ihr dokumentieren und umsetzen müsst.
      </div>
    </div>
  </div>

  <!-- ═══════ Phase 5: FR Checklist ═══════ -->
  <div *ngIf="phase === 5" class="phase-content">
    <h2>Foundational Requirements – Detailbewertung</h2>
    <p class="phase-desc">
      Beantworte die folgenden Fragen pro Foundational Requirement. Die Antworten bestimmen euer erreichtes Security Level (SL-A).
      <br>
      <em class="source-note">Quelle: Die 7 FRs sind in IEC TS 62443-1-1 definiert. Die konkreten System Requirements (SRs) pro FR und SL finden sich in IEC 62443-3-3.</em>
    </p>

    <div class="fr-section" *ngFor="let fr of frData">
      <div class="fr-header">
        <span class="fr-badge">{{fr.id}}</span>
        <span class="fr-title">{{fr.nameDe}}</span>
      </div>
      <p class="fr-desc">{{fr.description}}</p>

      <div class="fr-question" *ngFor="let q of fr.questions">
        <div class="fr-question-text">
          <span>{{q.text}}</span>
          <span class="sl-tag" [style.color]="slDescriptions[q.sl_relevant]?.color"
                [style.background]="(slDescriptions[q.sl_relevant]?.color || '#999') + '22'">
            ab SL {{q.sl_relevant}}
          </span>
        </div>
        <div class="fr-options">
          <button [class.yes]="frAnswers[q.id] === 'yes'" (click)="setFrAnswer(q.id, 'yes')">Ja</button>
          <button [class.partial]="frAnswers[q.id] === 'partial'" (click)="setFrAnswer(q.id, 'partial')">Teilw.</button>
          <button [class.no]="frAnswers[q.id] === 'no'" (click)="setFrAnswer(q.id, 'no')">Nein</button>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Phase 6: Documents ═══════ -->
  <div *ngIf="phase === 6" class="phase-content">
    <h2>Übergabe-Dokumentation</h2>
    <p class="phase-desc">Welche der folgenden Dokumente habt ihr für die Kundenübergabe erstellt oder verfügbar?</p>

    <div class="doc-list">
      <label class="doc-item" *ngFor="let doc of filteredDocs"
             [class.checked]="docChecks[doc.id]">
        <mat-checkbox [checked]="!!docChecks[doc.id]" (change)="toggleDoc(doc.id)">
          <div class="doc-text">
            <span class="doc-title">{{doc.text}}</span>
            <span class="doc-norm">
              Norm: {{doc.norm}}
              <span *ngIf="doc.required_sl > 1" class="sl-tag"
                    [style.color]="slDescriptions[doc.required_sl]?.color">
                ab SL {{doc.required_sl}}
              </span>
            </span>
          </div>
        </mat-checkbox>
      </label>
    </div>
  </div>

  <!-- ═══════ Phase 7: Results ═══════ -->
  <div *ngIf="phase === 7" class="phase-content">
    <h2>Assessment-Ergebnis</h2>

    <!-- SL Matrix -->
    <div class="result-card">
      <h3>Security Level Matrix</h3>
      <div class="sl-matrix">
        <div class="sl-matrix-item" [style.background]="(slDescriptions[targetSL]?.color || '#999') + '15'">
          <span class="sl-matrix-label">Ziel (SL-T)</span>
          <span class="sl-matrix-value" [style.color]="slDescriptions[targetSL]?.color">{{targetSL}}</span>
          <span class="sl-matrix-desc">{{slDescriptions[targetSL]?.title}}</span>
        </div>
        <div class="sl-matrix-item" [style.background]="(slDescriptions[achievedSL]?.color || '#999') + '15'">
          <span class="sl-matrix-label">Erreicht (SL-A, Schätzung)</span>
          <span class="sl-matrix-value" [style.color]="slDescriptions[achievedSL]?.color">{{achievedSL}}</span>
          <span class="sl-matrix-desc">{{slDescriptions[achievedSL]?.title}}</span>
        </div>
      </div>
      <div class="gap-alert" *ngIf="gap > 0">
        <mat-icon>warning</mat-icon>
        <span>Gap: {{gap}} Level{{gap > 1 ? 's' : ''}} – Es gibt Handlungsbedarf, um das Ziel-SL zu erreichen.</span>
      </div>
      <div class="gap-ok" *ngIf="gap === 0">
        <mat-icon>check_circle</mat-icon>
        <span>Das erreichte SL entspricht oder übertrifft das Ziel-SL.</span>
      </div>
      <p class="result-note">Hinweis: Das SL-A ist eine grobe Schätzung. Eine formale SL-Bestimmung erfordert eine vollständige Analyse gemäß IEC 62443-3-3.</p>
    </div>

    <!-- FR Overview -->
    <div class="result-card">
      <h3>Foundational Requirements – Übersicht</h3>
      <div class="fr-result" *ngFor="let fr of frData">
        <div class="fr-result-header">
          <span class="fr-result-name">{{fr.id}}: {{fr.nameDe}}</span>
          <span class="fr-result-pct" [style.color]="getFrPercentage(fr) >= 70 ? '#16a34a' : getFrPercentage(fr) >= 40 ? '#ca8a04' : '#dc2626'">
            {{getFrPercentage(fr)}}%
          </span>
        </div>
        <div class="fr-bar-track">
          <div class="fr-bar-fill" [style.width.%]="getFrPercentage(fr)"
               [style.background]="getFrPercentage(fr) >= 70 ? '#22c55e' : getFrPercentage(fr) >= 40 ? '#eab308' : '#ef4444'"></div>
        </div>
      </div>
    </div>

    <!-- CRA / IEC 62443-2-4 Status -->
    <div class="result-card">
      <h3>CRA &amp; Dienstleister-Anforderungen: {{nis2Count}} / {{nis2Measures.length}}</h3>
      <div class="nis2-bar-track">
        <div class="nis2-bar-fill" [style.width.%]="(nis2Count / nis2Measures.length) * 100"
             [style.background]="(nis2Count / nis2Measures.length) >= 0.8 ? '#22c55e' : (nis2Count / nis2Measures.length) >= 0.5 ? '#eab308' : '#ef4444'"></div>
      </div>
      <div *ngIf="nis2Measures.length - nis2Count > 0" class="missing-list">
        <p class="missing-label">Offene Anforderungen:</p>
        <div *ngFor="let m of nis2Measures">
          <span *ngIf="!answers[m.id]" class="missing-item">• {{m.text}} ({{m.article}})</span>
        </div>
      </div>
    </div>

    <!-- Missing Docs -->
    <div class="result-card">
      <h3>Dokumente: {{docCount}} / {{totalDocs}}</h3>
      <div *ngIf="missingDocs.length > 0" class="missing-list">
        <p class="missing-label">Noch zu erstellen:</p>
        <span *ngFor="let d of missingDocs" class="missing-item">• {{d.text}} ({{d.norm}})</span>
      </div>
    </div>

    <!-- Next Steps -->
    <div class="result-card highlight-blue">
      <h3><mat-icon>assignment</mat-icon> Nächste Schritte</h3>
      <ol class="next-steps">
        <li>Offene CRA-/IEC 62443-Anforderungen abarbeiten (siehe Liste oben)</li>
        <li>Fehlende Übergabe-Dokumente erstellen</li>
        <li>SBOM vervollständigen und dem Kunden bereitstellen</li>
        <li>Übergabeprotokoll + Integritätsnachweis (SHA-256) für das Projekt erstellen</li>
        <li>Härtungsleitfaden und Security-Konfiguration an den Kunden übergeben</li>
        <li>Patch-/Update-Prozess für gelieferte Komponenten mit dem Kunden abstimmen</li>
        <li>Schwachstellenbehandlungsprozess einrichten (CRA-Pflicht)</li>
      </ol>
    </div>

    <!-- Save Button -->
    <div class="save-section">
      <button mat-raised-button color="primary" (click)="saveAssessment()" [disabled]="saving" class="save-btn">
        <mat-spinner *ngIf="saving" diameter="20"></mat-spinner>
        <mat-icon *ngIf="!saving">save</mat-icon>
        Bewertung speichern & Anforderungsliste generieren
      </button>
      <p class="save-note">Die offenen Anforderungen werden als Checkliste im Projekt gespeichert und können einzeln abgehakt werden.</p>
    </div>
  </div>

  <!-- Navigation -->
  <div class="wizard-nav">
    <button mat-stroked-button (click)="prevPhase()" [disabled]="phase === 0">
      <mat-icon>chevron_left</mat-icon> Zurück
    </button>
    <button mat-raised-button color="primary" (click)="nextPhase()"
            [disabled]="!canProceed()" *ngIf="phase < phases.length - 1">
      Weiter <mat-icon>chevron_right</mat-icon>
    </button>
  </div>
</div>

<!-- Loading -->
<div *ngIf="loading" class="loading-container">
  <mat-spinner diameter="48"></mat-spinner>
</div>
